import { useMemo, useSyncExternalStore } from "react";

// A deliberately tiny history router: three screens don't justify a routing library.
// Paths are relative to the Vite base (e.g. /tools/freight-audit/).
const BASE = import.meta.env.BASE_URL.replace(/\/+$/, "");
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

export function toHref(to: string): string {
  return `${BASE}${to.startsWith("/") ? to : `/${to}`}`;
}

export function currentPath(): string {
  const path = window.location.pathname;
  const relative = BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path;
  return relative || "/";
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  const previousPath = currentPath();
  window.history[options.replace ? "replaceState" : "pushState"](null, "", toHref(to));
  listeners.forEach((listener) => listener());
  if (currentPath() !== previousPath) window.scrollTo({ top: 0 });
}

export function setSearchParam(name: string, value: string | null): void {
  const params = new URLSearchParams(window.location.search);
  if (value === null) params.delete(name);
  else params.set(name, value);
  const query = params.toString();
  navigate(`${currentPath()}${query ? `?${query}` : ""}`, { replace: true });
}

export function useLocation(): { path: string; search: URLSearchParams } {
  const location = useSyncExternalStore(subscribe, snapshot, snapshot);
  return useMemo(() => {
    const [, query = ""] = location.split("?");
    return { path: currentPath(), search: new URLSearchParams(query) };
  }, [location]);
}

export type Route =
  | { name: "new" }
  | { name: "history" }
  | { name: "audit"; auditId: string }
  | { name: "notFound" };

export function matchRoute(path: string): Route {
  const clean = path.replace(/\/+$/, "") || "/";
  if (clean === "/") return { name: "new" };
  if (clean === "/audits") return { name: "history" };
  const match = /^\/audits\/([0-9a-fA-F-]{36})$/.exec(clean);
  if (match?.[1]) return { name: "audit", auditId: match[1] };
  return { name: "notFound" };
}
